/**
 * Route guard, gated by PERMISSION CODE (not role -- a session can hold any
 * subset, E2-API-REFERENCE.md). Unauthenticated visitors are sent to /login;
 * an authenticated session missing the required permission is sent to
 * firstAllowedRoute(state.permissions) (routes.ts) -- the first nav section
 * it DOES have -- rather than a bare 404, so a demoted user lands somewhere
 * useful, not a dead end. Health (v1's permission-free landing page) is
 * removed from the UI, so no nav item needs zero permissions anymore;
 * firstAllowedRoute's floor for a session holding none of them is
 * ROUTES.notFound.
 */
import { Navigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.tsx';
import { firstAllowedRoute, ROUTES } from './routes.ts';

export interface RequirePermissionProps {
  /** Permission code required, or null for "any authenticated session". */
  permission: string | null;
  children: React.JSX.Element;
}

export function RequirePermission({ permission, children }: Readonly<RequirePermissionProps>): React.JSX.Element {
  const { isAuthenticated, state, hasPermission } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (permission !== null && !hasPermission(permission)) {
    return <Navigate to={firstAllowedRoute(state.permissions)} replace />;
  }
  return children;
}

export default RequirePermission;
